import React from 'react';
import { append, findIndex, isEmpty, propEq, update } from 'ramda';
import { Answer, Question } from '../../../types/types';
import Button from '../../Button/Button';
import CheckBox from '../../Inputs/CheckBox/CheckBox';
import Input from '../../Inputs/Input/Input';
import Modal from '../Modal';
import TextBoxInput from '../../Inputs/TextBoxInput/TextBoxInput';

type ModalProps = {
  open: boolean;
  question: Question;
  answers: Answer[];
  isUpdatingMedHistory: boolean;
  onCloseModal: () => void;
  onUpdateMedicalHistory: (answers: Answer[]) => void;
};

function MedicalHistoryForm({
  open,
  question,
  answers,
  isUpdatingMedHistory,
  onCloseModal,
  onUpdateMedicalHistory,
}: ModalProps) {
  const [providedAnswers, setProvidedAnswers] = React.useState<Answer[]>(answers);
  const [unAnsweredQuestions, setUnAnsweredQuestions] = React.useState<string[]>([]);

  const readAnsweredValue = (questionId: string) => {
    let ansValue = '';
    if (providedAnswers && providedAnswers.length > 0) {
      ansValue =
        providedAnswers.find(answer => answer.questionKey === questionId)?.answers[0] || '';
    }
    return ansValue;
  };

  const readAnsweredMultiValues = (questionId: string) => {
    let ansValue: string[] = [];
    if (providedAnswers && providedAnswers.length > 0) {
      ansValue =
        providedAnswers
          .find(answer => answer.questionKey === questionId)
          ?.answers?.map(fndAnswer => {
            if (fndAnswer.includes('OTHER ->')) {
              return 'OTHER';
            }

            return fndAnswer;
          }) || [];
    }
    return ansValue;
  };

  const onCheckInputChange = (value: string, checked: boolean, questionId: string) => {
    const checkedItems = readAnsweredMultiValues(questionId);

    if (!checked) {
      handleAnsweredQuestion({
        questionKey: questionId,
        answers: checkedItems.filter(fndItem => fndItem !== value),
      });
      return;
    }

    if (['NONE', 'SOMETHING_ELSE', 'OTHER'].includes(value)) {
      handleAnsweredQuestion({ questionKey: questionId, answers: [value] });
      return;
    }

    if (
      checkedItems.includes('NONE') ||
      checkedItems.includes('OTHER') ||
      checkedItems.includes('SOMETHING_ELSE')
    ) {
      handleAnsweredQuestion({
        questionKey: questionId,
        answers: [
          ...checkedItems.filter(fndItem => !['NONE', 'SOMETHING_ELSE', 'OTHER'].includes(fndItem)),
          value,
        ],
      });
      return;
    }

    handleAnsweredQuestion({
      questionKey: questionId,
      answers: [...checkedItems, value],
    });
  };

  const handleAnsweredQuestion = (answer: Answer) => {
    setProvidedAnswers(ans => {
      const tempAnsIndex = findIndex(propEq('questionKey', answer.questionKey), ans);
      if (tempAnsIndex === -1) {
        return append(answer, ans);
      }
      return update(tempAnsIndex, answer, ans);
    });

    setUnAnsweredQuestions(fndUnAnsweredQuestions => {
      if (!fndUnAnsweredQuestions || fndUnAnsweredQuestions.length === 0) {
        return [];
      }
      return fndUnAnsweredQuestions.filter(fndUnAnswered => fndUnAnswered !== answer.questionKey);
    });
  };

  const handleUpdateMedicals = async () => {
    const hasAnsweredQuestions = providedAnswers.find(
      answer =>
        answer.questionKey === question?.id &&
        answer.answers.filter(fndAnswer => !isEmpty(fndAnswer)).length > 0,
    );

    if (!hasAnsweredQuestions && question) {
      setUnAnsweredQuestions([question.id]);
      return;
    }

    onUpdateMedicalHistory(providedAnswers);
  };

  return (
    <Modal onClose={onCloseModal} show={open}>
      <div className="rounded-xl bg-white tablet:w-[550px]">
        <div className="flex flex-col p-6 pt-6 pb-10">
          <h3 className="text-lg font-semibold inline-flex whitespace-pre pb-4">
            Update Medical Information
          </h3>

          <hr className="mb-4" />

          <div className="flex flex-col px-2">
            <h3 className="text-sm leading-7 block mb-4 text-river-black font-medium">
              {question?.text || question?.option}
            </h3>
            {question.type === 'MULTIPLE-CHOICE' && question.options && (
              <div className="grid grid-cols-1 gap-3 mb-9">
                {question.options.map(input => (
                  <div key={input.id}>
                    <label
                      role="presentation"
                      key={input.id === 'NONE' ? `${question.id}_NONE` : input.id}
                      htmlFor={input.id === 'NONE' ? `${question.id}_NONE` : input.id}
                      className="text-sm block pl-[18px] -indent-[18px] cursor-pointer"
                    >
                      <CheckBox
                        id={input.id === 'NONE' ? `${question.id}_NONE` : input.id}
                        name={input.id === 'NONE' ? `${question.id}_NONE` : input.id}
                        value={input.id}
                        checked={readAnsweredMultiValues(question.id).includes(input.id)}
                        onChange={event =>
                          onCheckInputChange(event.target.value, event.target.checked, question.id)
                        }
                        className="align-middle cursor-pointer"
                      />{' '}
                      <span className="align-middle font-normal">{input.text}</span>
                    </label>

                    {readAnsweredMultiValues(question.id).includes(input.id) &&
                      input.id === 'OTHER' && (
                        <Input
                          className="fs-exclude mt-2"
                          name={input.id}
                          placeholder={input.hint || 'Type here...'}
                          defaultValue={readAnsweredValue(question.id)
                            .replace('OTHER -> ', '')
                            .trim()}
                          onChange={event => {
                            handleAnsweredQuestion({
                              questionKey: question.id,
                              answers: [`${input.id} -> ${event.target.value}`],
                            });
                          }}
                        />
                      )}
                  </div>
                ))}

                {question.error && unAnsweredQuestions.includes(question.id) && (
                  <span className="text-river-red text-left text-xs !mt-[-5px] inline-flex">
                    {question.error}
                  </span>
                )}
              </div>
            )}

            {question.type === 'OPEN-TEXT' && (
              <div className="grid grid-cols-1 gap-3 mb-9">
                <Input
                  className="fs-exclude"
                  id={question.id}
                  name={question.id}
                  placeholder="Type here..."
                  value={readAnsweredValue(question.id)}
                  onChange={event => {
                    handleAnsweredQuestion({
                      questionKey: question.id,
                      answers: [event.target.value],
                    });
                  }}
                />

                {question.error && unAnsweredQuestions.includes(question.id) && (
                  <span className="text-river-red text-left text-xs mt-1 inline-flex">
                    {question.error}
                  </span>
                )}
              </div>
            )}

            {question.type === 'OPEN-TEXTAREA' && (
              <div className="grid grid-cols-1 gap-3 mb-9">
                <TextBoxInput
                  className="fs-exclude"
                  id={question.id}
                  name={question.id}
                  placeholder="Type here..."
                  rows={4}
                  value={readAnsweredValue(question.id)}
                  onChange={event => {
                    handleAnsweredQuestion({
                      questionKey: question.id,
                      answers: [event.target.value],
                    });
                  }}
                />

                {question.error && unAnsweredQuestions.includes(question.id) && (
                  <span className="text-river-red text-left text-xs mt-1 inline-flex">
                    {question.error}
                  </span>
                )}
              </div>
            )}

            <div className="w-full mt-auto text-center">
              <Button
                label="Update"
                disabled={isUpdatingMedHistory}
                loading={isUpdatingMedHistory}
                onClick={handleUpdateMedicals}
                className="w-[200px] !rounded-full !py-[10px]"
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default MedicalHistoryForm;
